import React from "react";
import MuiAutocomplete from "@mui/material/Autocomplete";
import PropTypes from "prop-types";

/**
 * Autocomplete component wrapper for editable combobox selections.
 * Wraps Material-UI Autocomplete with theme integration.
 *
 * Unlike a native <select>, the Autocomplete renders a real text <input>,
 * so the selected value is visible as text and can be selected/copied.
 *
 * @component
 * @example
 * <Autocomplete
 *   options={options}
 *   value={value}
 *   onChange={handleChange}
 *   renderInput={(params) => <TextField {...params} label="Plan" />}
 * />
 */
const Autocomplete = (props) => {
  return <MuiAutocomplete {...props} />;
};

Autocomplete.propTypes = {
  /**
   * Array of options to be displayed in the dropdown
   */
  options: PropTypes.array,
  /**
   * The value of the autocomplete
   */
  value: PropTypes.any,
  /**
   * The default value. Use when the component is not controlled
   */
  defaultValue: PropTypes.any,
  /**
   * Used to determine the string value for a given option
   */
  getOptionLabel: PropTypes.func,
  /**
   * Used to determine if the option represents the given value
   */
  isOptionEqualToValue: PropTypes.func,
  /**
   * A filter function that determines the options that are displayed
   */
  filterOptions: PropTypes.func,
  /**
   * Render the input. `params` should be spread onto the input component
   */
  renderInput: PropTypes.func,
  /**
   * Callback fired when the value changes
   */
  onChange: PropTypes.func,
  /**
   * Callback fired when the text input value changes
   */
  onInputChange: PropTypes.func,
  /**
   * If true, the input can't be cleared via the clear indicator
   */
  disableClearable: PropTypes.bool,
  /**
   * If true, the user can enter values that do not exist in the options
   */
  freeSolo: PropTypes.bool,
  /**
   * If true, the component is disabled
   */
  disabled: PropTypes.bool,
  /**
   * If true, the input will take up the full width of its container
   */
  fullWidth: PropTypes.bool,
  /**
   * The size of the component
   */
  size: PropTypes.oneOf(["small", "medium"]),
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles
   */
  sx: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])
    ),
    PropTypes.func,
    PropTypes.object,
  ]),
};

// No defaultProps - maintain exact MUI Autocomplete behavior
export default Autocomplete;
